import { ArrowDown, ArrowRight, Check, Paperclip, X } from '@phosphor-icons/react';
import { ComposerReferenceChips, ComposerReferenceMenu } from '../ComposerReferencePicker';
import { COMPOSER_FILE_ACCEPT, formatComposerFileSize } from './attachmentValidation';
import './PromptComposer.css';

export default function PromptComposer({
  name = 'Prompt composer',
  location = 'composer',
  placeholder = 'What should this workflow do?',
  promptText,
  onPromptChange,
  onKeyDown,
  onSubmit,
  textareaRef,
  picker,
  references = [],
  onRemoveReference,
  attachments = [],
  onRemoveAttachment,
  attachmentsLoading = false,
  fileInputRef,
  onFileInput,
  onAttachClick,
  isSubmitting = false,
  submitFeedback = null,
  isDragging = false,
  canSubmit = false,
  errorMessage,
  hintMessage,
  onComposerFocus,
  toolbarControls,
  submitLabel = 'Submit',
  submittingLabel = 'Starting…',
  dropHint = 'Drop files to attach them.',
}) {
  const statusId = `prompt-composer-status-${location}`;
  const hasStatus = Boolean(errorMessage || hintMessage);
  const handlePromptChange = (event) => {
    event.currentTarget.style.height = 'auto';
    event.currentTarget.style.height = `${Math.min(event.currentTarget.scrollHeight, 300)}px`;
    onPromptChange(event);
  };
  return <form className="prompt-composer-form" onSubmit={onSubmit} data-location={location} aria-busy={isSubmitting || undefined} aria-label={name}>
    <div className={`prompt-composer${isDragging ? ' prompt-composer--drag' : ''}`}>
      {isDragging && <div className="prompt-composer__drop-hint" aria-hidden="true">{dropHint}</div>}
      <div className="prompt-composer__menu-wrap">
        <textarea
          ref={textareaRef}
          className="prompt-composer__textarea"
          placeholder={placeholder}
          aria-label={name}
          aria-describedby={hasStatus ? statusId : undefined}
          value={promptText}
          onChange={handlePromptChange}
          onSelect={picker?.handleSelection}
          onKeyDown={onKeyDown}
          onFocus={onComposerFocus}
          disabled={isSubmitting}
        />
        {picker && <ComposerReferenceMenu picker={picker} className="prompt-composer-reference-menu" />}
      </div>
      {(references.length > 0 || attachments.length > 0 || attachmentsLoading) && <div className="prompt-composer__chips">
        {references.length > 0 && <ComposerReferenceChips references={references} className="prompt-composer-reference-chips" disabled={isSubmitting} onRemove={onRemoveReference} />}
        {attachments.map((attachment) => <div key={attachment.id} className="prompt-composer-chip">
          <span className="prompt-composer-chip__ext">{attachment.extension}</span>
          <span className="prompt-composer-chip__meta"><span className="prompt-composer-chip__name">{attachment.name}</span><span>{formatComposerFileSize(attachment.size)}</span></span>
          <button type="button" className="prompt-composer-chip__remove" onClick={() => onRemoveAttachment(attachment.id)} disabled={isSubmitting} aria-label={`Remove ${attachment.name}`}><X size={12} weight="bold" /></button>
        </div>)}
        {attachmentsLoading && <span className="prompt-composer-chip prompt-composer-chip--skeleton" aria-hidden="true" />}
      </div>}
      <input ref={fileInputRef} type="file" hidden multiple accept={COMPOSER_FILE_ACCEPT} onChange={onFileInput} aria-label="Choose files to attach" />
      <div className="prompt-composer__toolbar">
        <div className="prompt-composer__toolbar-group">
          <button type="button" className="prompt-composer__tool prompt-composer__attach" onClick={onAttachClick} disabled={isSubmitting} aria-label="Attach files"><Paperclip size={17} /></button>
          {toolbarControls}
        </div>
        {submitFeedback ? (
          <div className={`prompt-composer__submit-toast is-${submitFeedback}`} role="status" aria-live="polite">
            <span className="prompt-composer__submit-toast-icon" aria-hidden="true">
              <Check className="prompt-composer__submit-toast-check" size={17} weight="bold" />
              <ArrowDown className="prompt-composer__submit-toast-arrow" size={17} weight="bold" />
            </span>
            <span key={submitFeedback} className="prompt-composer__submit-toast-message">
              {submitFeedback === 'success' ? 'Task submitted successfully!' : 'View progress below'}
            </span>
          </div>
        ) : (
          <button type="submit" className="prompt-composer__submit" aria-label={isSubmitting ? submittingLabel : submitLabel} disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? <><span className="prompt-composer__spinner" aria-hidden="true" /><span className="prompt-composer__sr-only">{submittingLabel}</span></> : <ArrowRight size={18} weight="bold" aria-hidden="true" />}
          </button>
        )}
      </div>
      {hasStatus && <p id={statusId} className={`prompt-composer__status${errorMessage ? ' is-error' : ''}`} role={errorMessage ? 'alert' : 'status'} aria-live="polite">{errorMessage || hintMessage}</p>}
    </div>
  </form>;
}
